import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';

import styled, { useTheme } from 'styled-components';

// Store
import AppState from '@webapp/store/state';
import { CloseMenu } from '@webapp/store/ui/action-creators';

// Components
import NewMenu from '@components/new-menu';
import Flex from '@components/layout/flex';

// Controllers
import UserAvatarInfo from '@components/basics/user-avatar-info';
import { Deal } from '@services/deals/types/deal';
import { DealSummaryTitle } from '../../label/deal-management/summary/components';
import Typography from '../../../components/basics/typography';
import ProgressbarInline from '../../../components/basics/progressbar-inline';

interface DealDetailsMenuProps {
  className?: string;
  isMobile?: boolean;
  closeMenu: () => CloseMenu;
  deal?: Deal;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
  deal:
    state.deals.detail ||
    state.deals.participating.find(
      (deal) => deal.type === 'exclusive-license-with-advance'
    ),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
});

const getDealParticipant = (deal: Deal): { avatar?: string; name: string } => {
  if (deal.type === 'label-agreement') {
    if (deal.labels?.length) {
      return {
        avatar: deal.labels[0].avatarUrl,
        name: deal.labels[0].name,
      };
    }
  }
  if (deal.artists?.length) {
    return {
      avatar: deal.artists[0].avatarUrl,
      name: deal.artists[0].name,
    };
  }

  if (deal.isrcs?.length) {
    return {
      name: deal.notes || deal.isrcs[0].title,
      avatar: deal.isrcs[0].coverArtUrl,
    };
  }

  return {
    name: deal.notes || deal.id,
  };
};

export const DealDetailsMenu: React.FC<DealDetailsMenuProps> = ({
  className,
  isMobile,
  deal,
}) => {
  const theme = useTheme();

  if (!deal) {
    return null;
  }

  const { avatar, name } = getDealParticipant(deal);

  const postRecoupmentRate =
    deal.postRecoupmentBeneficiaries?.reduce((m, b) => m + b.share, 0) || 0;

  return (
    <NewMenu
      className={className}
      noButton
      menuId="DEAL_DETAILS"
      topHeader={isMobile && <UserAvatarInfo src={avatar} name={name} />}
      header={!isMobile && <UserAvatarInfo src={avatar} name={name} />}
    >
      <Form autoComplete="off" onSubmit={(ev) => ev.preventDefault()}>
        <Content>
          <DealSummaryRow>
            <DealSummaryEntry>
              <DealSummaryTitle
                type="small"
                weight="700"
                color={theme.colors.tertiary}
                uppercase
              >
                <Translate id="LABEL.DEALS.SUMMARY.ADVANCE.ADVANCE" />
              </DealSummaryTitle>
              <Typography customFont="semi-bold" type="section-title-preview">
                {deal.advance?.advance.toFormat()}
              </Typography>
            </DealSummaryEntry>
            <DealSummaryEntry>
              <DealSummaryTitle
                type="small"
                weight="700"
                color={theme.colors.tertiary}
                uppercase
              >
                <Translate id="LABEL.DEALS.SUMMARY.ADVANCE.RECOUPMENT" />
              </DealSummaryTitle>
              <Typography customFont="semi-bold" type="section-title-preview">
                {deal.recoupment?.toFormat()}
              </Typography>
            </DealSummaryEntry>
          </DealSummaryRow>

          <DealSummaryRow>
            <ProgressWrapper>
              <StyledProgress
                isFullWidth
                progress={
                  ((deal.recoupment?.toUnit() || 0) /
                    (deal.advance?.advance.toUnit() || 1)) *
                  100
                }
              />
            </ProgressWrapper>
          </DealSummaryRow>

          <DealSummaryRow>
            <DealSummaryEntry>
              <DealSummaryTitle
                type="small"
                weight="700"
                color={theme.colors.tertiary}
                uppercase
              >
                <Translate id="LABEL.DEALS.SUMMARY.ADVANCE.RECOUPMENT_RATE" />
              </DealSummaryTitle>
              <Typography customFont="semi-bold" type="section-title-preview">
                {deal.advance?.recoupmentRate}%
              </Typography>
            </DealSummaryEntry>
            <DealSummaryEntry>
              <DealSummaryTitle
                type="small"
                weight="700"
                color={theme.colors.tertiary}
                uppercase
              >
                <Translate id="LABEL.DEALS.SUMMARY.ADVANCE.POST_RECOUPMENT_RATE" />
              </DealSummaryTitle>
              <Typography customFont="semi-bold" type="section-title-preview">
                {postRecoupmentRate}%
              </Typography>
            </DealSummaryEntry>
          </DealSummaryRow>
          <DealSummaryRow>
            <DealSummaryEntry>
              <DealSummaryTitle
                type="small"
                weight="700"
                color={theme.colors.tertiary}
                uppercase
              >
                <Translate id="LABEL.DEALS.SUMMARY.ADVANCE.LENGTH" />
              </DealSummaryTitle>
              <Typography customFont="semi-bold" type="section-title-preview">
                {deal.duration.term} {deal.duration.frequency}
              </Typography>
            </DealSummaryEntry>
            <DealSummaryEntry>
              <DealSummaryTitle
                type="small"
                weight="700"
                color={theme.colors.tertiary}
                uppercase
              >
                <Translate id="LABEL.DEALS.SUMMARY.ADVANCE.WORKS" />
              </DealSummaryTitle>
              <Typography customFont="semi-bold" type="section-title-preview">
                {deal.isrcCount || 0} songs
              </Typography>
            </DealSummaryEntry>
          </DealSummaryRow>
        </Content>
      </Form>
    </NewMenu>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(DealDetailsMenu);

const Form = styled.form`
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

const ProgressWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledProgress = styled(ProgressbarInline)`
  background-color: ${({ theme }) => theme.colors.lightGray};
  &:after {
    background-color: ${({ theme }) => theme.colors.blueColor};
  }
`;

export const DealSummaryRow = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;
  margin-bottom: 3rem;
`;

const DealSummaryEntry = styled.div`
  width: 50%;
  text-align: left;
`;
